import React from "react";

import { FeatureCollection } from "geojson";
import { toast } from "react-toastify";
import { IReactionDisposer, makeAutoObservable, reaction } from "mobx";

import useVM from "@src/hooks/useVM";
import { RootStore, useStores } from "@stores";
import { userDevices, AttachedDevice, BBox } from "@services/devicesService";
import { getFieldsData } from "@services/fieldsService";
import { repairGeometryForDisplay } from "@src/utils/repairGeometry";

const ctx = React.createContext<Context | null>(null);

export class Context {
  // Context state
  loading: boolean = true;
  setLoading = (v: boolean) => (this.loading = v);

  // Search query
  query: string = "";
  setQuery = (query: string) => (this.query = query);

  // User fields
  fields: FeatureCollection = { type: "FeatureCollection", features: [] };
  private setFields = (v: FeatureCollection) => (this.fields = v);

  // List of user devices
  devices: AttachedDevice[] = [];
  private setDevices = (v: AttachedDevice[]) => (this.devices = v);

  bbox: BBox | null = null;
  private setBBox = (v: BBox | null) => (this.bbox = v);

  // The season reaction, registered once the first load finishes. A screen
  // left before that must not register it at all — hence `disposed`.
  private seasonReactionDisposer: IReactionDisposer | null = null;
  private disposed = false;

  constructor(private rootStore: RootStore) {
    makeAutoObservable<Context, "seasonReactionDisposer" | "disposed">(this, {
      seasonReactionDisposer: false,
      disposed: false,
    });
    console.time("loading");
    this.loadUserFields(this.rootStore.seasonStore.currentSeasonId)
      .finally(() => this.loadUserDevices())
      .finally(() => {
        if (this.disposed) return;
        this.seasonReactionDisposer = reaction(
          () => [this.rootStore.seasonStore.currentSeasonId],
          () => {
            this.setLoading(true);
            this.loadUserFields(this.rootStore.seasonStore.currentSeasonId)
              .finally(() => this.setLoading(false));
          }
        );
        this.setLoading(false)
        console.timeEnd("loading");
      });
  }

  /** Stop reacting to season changes once the Tracks screen is gone. Idempotent. */
  dispose = () => {
    this.disposed = true;
    this.seasonReactionDisposer?.();
    this.seasonReactionDisposer = null;
  };

  // Load user devices and save them to the 'devices' property
  async loadUserDevices() {
    try {
      const response = await userDevices();
      this.setDevices(response.devices);
      this.setBBox(response.bbox);
    } catch (e: any) {
      console.error("ERROR [API.Devices]:", e);
      toast.error(`Failed to get devices list: ${e.message}`);
      throw e;
    }
  }

  // Load user fields and save
  async loadUserFields(season_id?: string) {
    // The field-boundary overlay is off for a team role without fields & yield
    // maps — the backend would 403 the call (FIELDS_NOT_ALLOWED).
    if (!this.rootStore.accountStore.canViewFields) return;
    try {
      const response = await getFieldsData(season_id);
      // Repair interior-ring geometry for the Mapbox renderer (display-only).
      // Mapbox's earcut tessellation produces artifacts on JD exclusion-zone
      // boundaries whose rings touch/cross the exterior; a self-union fixes it.
      // New features are built — the fetched data is left untouched.
      const fc = response.features;
      const repaired = {
        type: "FeatureCollection",
        features: fc.features.map((f) => ({
          ...f,
          geometry: repairGeometryForDisplay(f.geometry),
        })),
      };
      this.setFields(repaired as unknown as FeatureCollection);
    } catch (e: any) {
      console.error("ERROR [API.Fields]:", e);
      toast.error(`Failed to fields list: ${e.message}`);
    }
  }

  // getDevices returns list of devices with search query applied
  getDevices(): AttachedDevice[] {
    const query = this.query.trim().toLowerCase();
    if (query.length === 0) {
      return this.devices;
    }

    return this.devices.filter((device) => {
      if (device.getName().toLowerCase().includes(query)) {
        return true;
      }
      if (device.getDescription().toLowerCase().includes(query)) {
        return true;
      }
      if (device.getEsn().toLowerCase().includes(query)) {
        return true;
      }
      return false;
    });
  }
};

// Contenxt provider for the the tracks screen state.
export const ContextProvider: React.FC = ({ children }) => {
  // TODO(nk2ge5k): why do we need rootStore here?
  const rootStore = useStores();
  const store = React.useMemo(() => new Context(rootStore), [rootStore]);
  // Without this every visit left a reaction refetching fields on each season
  // change for a screen nobody was looking at.
  React.useEffect(() => () => store.dispose(), [store]);
  return <ctx.Provider value={store}>{children}</ctx.Provider>;
};

export const useContext = () => useVM(ctx);
